import React, { Component } from 'react';
import { Resizable } from 're-resizable';
import { TrevorIcon } from '@trevor/ui-components';
import { SIDEBAR_ANIMATION_FINISHED } from '@/constants/custom-event-names';
import { createCustomEvent } from '@/util/createCustomEvent';
import { userAction } from '@/util/helpers/ajax-helpers';
import buttons from '../css/module/buttons.css';
import cssNewWorld from '../css/module/new-world.css';

const sidebarAnimationEvent = createCustomEvent(SIDEBAR_ANIMATION_FINISHED);

//
// <Resizable> sets scrollTop to 0 whenever resizing. We don't want that to happen
// (as loses scroll position) so when it does, we reset it back to its previous position
// by adding an onscroll function (see existing uses of this component e.g. schemanav)
//

class ResizeableSidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      width: props.initialWidth,
      isOpen: props.isVisible,
      isTransitioning: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { isVisible } = this.props;

    if (isVisible && !prevProps.isVisible) {
      this.toggleSidebar(true);
    } else if (!isVisible && prevProps.isVisible) {
      this.toggleSidebar(false);
    }
  }

  updateWidth = (d) => {
    this.setState((state) => ({
      width: state.width + d.width,
    }));
    userAction('use-sidebar-drag');
  };

  onOpen = (value) => {
    this.setState({
      isOpen: value,
    });
  };

  isTransitioning = (value) => {
    this.setState({
      isTransitioning: value,
    });
  };

  toggleSidebar = (value) => {
    const { isRightSidebar, onCloseDone, onOpenDone, onSidebarToggle } =
      this.props;

    this.onOpen(value);
    // if it is not wrapped, wrap it.

    this.isTransitioning(true);
    // if it is wrapped, unwrap it
    // unwrap the sidebar AFTER the css transition
    setTimeout(() => {
      this.isTransitioning(false);
      if (value && onOpenDone) onOpenDone();
      if (!value && onCloseDone) onCloseDone();
      onSidebarToggle?.(value);
      window.dispatchEvent(sidebarAnimationEvent);
    }, 500); // length of css transition

    if (!isRightSidebar) {
      userAction('use-sidebar-toggle');
    }
  };

  render() {
    const {
      isRightSidebar,
      isVisible,
      className,
      withToggleButton = false,
    } = this.props;

    // the toggle to open and close the sidebar.
    // hide the button for the chartSidebar when not in charting mode
    const sidebarToggle = !isVisible ? null : (
      <div
        className={`${buttons.btn} ${buttons.circle} ${
          cssNewWorld.sidebarToggle
        } ${isRightSidebar ? cssNewWorld.chartOptionToggle : ''}`}
        onClick={() => this.toggleSidebar(!this.state.isOpen)}
      >
        <TrevorIcon
          name="arrow-small-left"
          style={{
            transform: this.state.isOpen ? '' : 'rotate(0.5turn)',
          }}
        />
      </div>
    );

    // value when false must equal min-value set in class for closed sidebar
    const minWidth =
      this.props.isVisible && !this.state.isOpen
        ? 20
        : this.props.isVisible && this.state.isOpen
        ? this.state.isTransitioning
          ? 20
          : 200
        : 0;

    // set maxWidth when closed
    const toggleClass = this.state.isOpen
      ? ''
      : cssNewWorld.chartOptionsSideBarClosed;
    // add css transitions while sidebar is wrapped
    const transitionClass = this.state.isTransitioning
      ? cssNewWorld.transition
      : '';

    // if sidebar is closed, wrap it in div with overflow hidden. This means items don't get visibly squashed during the open / close transition.
    // a width is passed in if it's wrapped as, when used in newworld, the stepsidebar component needs to know when to close a dropdown menu.
    const content =
      !this.props.isVisible && !this.state.isTransitioning ? null : !this.state
          .isTransitioning && this.state.isOpen ? (
        this.props.children
      ) : (
        <div className={cssNewWorld.hiddenContainer}>
          <div style={{ width: `${this.state.width}px` }}>
            {this.props.children}
          </div>
        </div>
      );

    const grabAreaStyle = {
      position: 'absolute',
      userSelect: 'none',
      width: '13px',
      height: '100%',
      top: '0px',
      cursor: 'ew-resize',
      zIndex: 2,
    };

    const enableDrag = {
      top: false,
      bottom: false,
      right: true,
      left: false,
      topRight: false,
      bottomRight: false,
      bottomLeft: false,
      topLeft: false,
    };

    if (isRightSidebar) {
      grabAreaStyle.left = '-5px';
      enableDrag.left = true;
      enableDrag.right = false;
    } else {
      grabAreaStyle.right = '-12px';
    }

    return (
      <Resizable
        className={`${
          cssNewWorld.leftSection
        } ${toggleClass} ${transitionClass} ${cssNewWorld.hiddenDuringPrint} ${
          isRightSidebar ? cssNewWorld.chartSidebar : ''
        } ${
          !isRightSidebar && !isVisible ? cssNewWorld.noRightBorder : ' '
        } ${className}`}
        size={{ width: this.state.width }}
        onResizeStop={(e, direction, ref, d) => this.updateWidth(d)}
        minWidth={minWidth}
        enable={enableDrag}
        maxWidth="60%"
        handleStyles={{ [isRightSidebar ? 'left' : 'right']: grabAreaStyle }} // to appear above topleftcell of table
        handleClasses={{
          [isRightSidebar
            ? 'left'
            : 'right']: `${cssNewWorld.resizeArea} ${cssNewWorld.resizeFadeOut}`,
        }}
      >
        {content}
        {withToggleButton && sidebarToggle}
      </Resizable>
    );
  }
}

export default ResizeableSidebar;
